import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Landing from "@/pages/Landing";
import Setup from "@/pages/Setup";
import SessionPage from "@/pages/SessionPage";
import Admin from "@/pages/Admin";
import Scoring from "@/pages/Scoring";
import Compare from "@/pages/Compare";
import Welcome from "@/pages/Welcome";
import Account from "@/pages/Account";
import Meetings from "@/pages/Meetings";
import Privacy from "@/pages/Privacy";
import { AuthContext, UserContext, isAdmin, isPasswordAdmin, useAuth, useUser } from "@/lib/user";

// Who is signed in, asked once per load. Pages read the person through UserContext, provided by the guards below.
function AuthProvider({ children }) {
  const [state, setState] = useState({ phase: "checking", user: null });
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setState({ phase: "ok", user: data.user })).catch(() => setState({ phase: "no", user: null }));
  }, []);
  const setUser = (user) => setState((s) => ({ ...s, user }));
  const signIn = (user) => setState({ phase: "ok", user }); // after the sign-in pages succeed, without a reload
  return <AuthContext.Provider value={{ ...state, setUser, signIn }}>{children}</AuthContext.Provider>;
}

const Blank = () => <div className="h-screen bg-[var(--o-bg)]" data-testid="auth-checking" />;

function RequireAuth() {
  const { phase, user, setUser } = useAuth();
  if (phase === "checking") return <Blank />;
  if (phase === "no") return <Navigate to={window.location.pathname.startsWith("/admin") ? "/admin/login" : "/login"} replace />;
  return <UserContext.Provider value={{ ...user, setUser }}><Outlet /></UserContext.Provider>;
}

// "/" is the landing page for a visitor and the start page for someone signed in.
function Home() {
  const { phase, user, setUser } = useAuth();
  if (phase === "checking") return <Blank />;
  if (phase === "no") return <Landing />;
  if (isPasswordAdmin(user)) return <Navigate to="/admin" replace />;
  return <UserContext.Provider value={{ ...user, setUser }}><Setup /></UserContext.Provider>;
}

// Meetings, briefings and the account page need a person; the admin password has none of those.
function RequirePerson() {
  const user = useUser();
  return isPasswordAdmin(user) ? <Navigate to="/admin" replace /> : <Outlet />;
}

// Members who follow an admin link land on their own start page instead of an error.
function RequireAdmin() {
  const user = useUser();
  return isAdmin(user) ? <Outlet /> : <Navigate to="/" replace />;
}

export default function App() {
  const [theme] = useTheme();
  return (
    <TooltipProvider delayDuration={150}>
      <BrowserRouter>
        <Toaster theme={theme} position="bottom-left" toastOptions={{ style: { background: "var(--o-bg2)", color: "var(--o-fg)", border: "1px solid var(--o-border)", borderRadius: 2, fontFamily: "Chivo" } }} />
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route element={<RequireAuth />}>
              <Route element={<RequirePerson />}>
                <Route path="/session/:id" element={<SessionPage />} />
                <Route path="/account" element={<Account />} />
                <Route path="/meetings" element={<Meetings />} />
                <Route path="/score/:id" element={<Scoring />} />
                <Route path="/compare" element={<Compare />} />
              </Route>
              <Route element={<RequireAdmin />}>
                <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
                <Route path="/admin/:section" element={<Admin />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  );
}
